import React, { useState, useEffect, useContext } from "react";
import api from "../utils/base_url";
import { AuthContext } from "../context/AuthContext";
import toast from "react-hot-toast";
import '../styles/ProfileUpdateForm.css';

const ProfileUpdateForm = () => {
  const { user, access } = useContext(AuthContext);
  const [initialData, setInitialData] = useState(null);
  const [formData, setFormData] = useState({

    full_name: "",
    door: "",
    street: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    dob: "",
    gender: "",
    profile_image: null,
    manual_entry: false,
  });

  const [preview, setPreview] = useState(null);
  const [pincodeValid, setPincodeValid] = useState(false);

  useEffect(() => {
    if (user?.customer_details?.id && access) {
      (async () => {
        try {
          const res = await api.get(`/customerslist/${user.customer_details.id}/`);
          const data = res.data;
          const address = data.data.address || user.customer_details.address || "";
          const parsed = parseAddress(address);

          setFormData({
            full_name: data.data.full_name || "",
            ...parsed,
            dob: data.data.dob || "",
            gender: data.data.gender || "",
            profile_image: null
          });

          setInitialData({
            full_name: data.data.full_name || "",
            ...parsed,
            dob: data.data.dob || "",
            gender: data.data.gender || "",
            profile_image: null
          });

          if (data.data.profile_image) {
            const imgUrl = data.data.profile_image.startsWith("http")
              ? data.data.profile_image
              : `${data.data.profile_image}`;
            setPreview(imgUrl);
          } else if (user.login_method === "google" && user.customer_details.profile_image) {
            setPreview(user.customer_details.profile_image);
          } else if (user.profile_image) {
            setPreview(user.profile_image);
          }

          if (parsed.pincode?.length === 6) setPincodeValid(true);
        } catch (err) {
          console.error("Failed to load customer:", err);
        }
      })();
    }
  }, [user, access]);

  const parseAddress = (address = "") => {
    const parts = address.split(",").map(p => p.trim());
    let city = "", state = "", pincode = "";

    parts.forEach(p => {
      const pinMatch = p.match(/(.+)\s*-\s*(\d{6})/);
      if (pinMatch) {
        state = pinMatch[1].trim();
        pincode = pinMatch[2];
      }
    });

    const pinIndex = parts.findIndex(p => p.includes(pincode));
    if (pinIndex > 0) city = parts[pinIndex - 1];

    return {
      door: parts[0] || "",
      street: parts[1] || "",
      area: parts[2] || "",
      landmark:
        parts.find(p => p.toLowerCase().startsWith("landmark"))
          ?.replace("Landmark:", "").trim() || "",
      city,
      state,
      pincode
    };
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profile_image" && files?.[0]) {
      setFormData({ ...formData, profile_image: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const fetchAddressFromPincode = async (pincode) => {
    if (!/^\d{6}$/.test(pincode)) return;
    try {
      const res = await fetch(
        `https://pincode.scaleupstores.com/api/v1/pincode/lookup/${pincode}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": "my_secure_api_key_2026",
          },
        }
      );
      const data = await res.json();

      if (data.status) {
        setFormData(prev => ({
          ...prev,
          city: data.district || "",
          state: data.state || "",
          manual_entry: false,
        }));
        setPincodeValid(true);
        return;
      }

      toast.error("Pincode not found. Please enter manually.");
      setFormData(prev => ({ ...prev, city: "", state: "", manual_entry: true }));
      setPincodeValid(false);
    } catch (error) {
      console.error(error);
      toast.error("Pincode service unavailable. Please enter manually.");
      setFormData(prev => ({ ...prev, city: "", state: "", manual_entry: true }));
      setPincodeValid(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fullAddress = [
      formData.door,
      formData.street,
      formData.area,
      formData.landmark && `Landmark: ${formData.landmark}`,
      `${formData.city}, ${formData.state} - ${formData.pincode}`,
      "India"
    ].filter(Boolean).join(", ");

    const fd = new FormData();
    fd.append("full_name", formData.full_name);
    fd.append("address", fullAddress);
    fd.append("dob", formData.dob);
    fd.append("gender", formData.gender);
    if (formData.profile_image) fd.append("profile_image", formData.profile_image);

    try {
      if (user?.customer_details?.id) {
        await api.put(`customerdetails/${user.customer_details.id}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("customerdetails/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success("Profile saved successfully!");

      const modalEl = document.getElementById("profileModal");
      const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
      modalInstance.hide();

      setTimeout(() => {
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
        const backdrop = document.querySelector(".modal-backdrop");
        if (backdrop) backdrop.remove();
      }, 100);
    } catch (err) {
      console.error(err);
      toast.error("Error saving profile. Please try again.");
    }
  };

  const resetForm = () => {
    if (initialData) {
      setFormData(initialData);
      if (user?.customer_details?.profile_image) setPreview(user.customer_details.profile_image);
      else setPreview(null);
    }
  };

  return (
    <div
      className="modal fade"
      id="profileModal"
      tabIndex="-1"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={handleSubmit} className="profile-form">

            {/* ── HEADER ── */}
            <div className="modal-header">
              <h5 className="modal-title profile-modal-title">
                Update Profile
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              />
            </div>

            {/* ── AVATAR STRIP ── */}
            {preview && (
              <div className="profile-avatar-section">
                <img
                  src={preview}
                  alt="Profile preview"
                  className="profile-preview-img"
                />
                <div className="profile-avatar-meta">
                  <div className="profile-avatar-name">
                    {formData.full_name || "Your Name"}
                  </div>
                  <div className="profile-avatar-hint">
                    {user?.login_method === "google"
                      ? "Profile picture synced from Google"
                      : "Tap below to change your photo"}
                  </div>
                </div>
              </div>
            )}

            {/* ── BODY ── */}
            <div className="modal-body">
              <div className="row g-3">

                {/* SECTION: Personal */}
                <div className="col-12">
                  <div className="form-section-label">Personal Info</div>
                </div>

                {/* Full Name */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="fullName">Full Name</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="fullName"
                      name="full_name"
                      value={formData.full_name}
                      maxLength="60"
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="dob">Date of Birth</label>
                    <input
                      type="date"
                      className="form-control themed-input"
                      id="dob"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      placeholder="Date of Birth"
                    />
                  </div>
                </div>

                {/* Gender */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="gender">Gender</label>
                    <select
                      className="form-select themed-input"
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* SECTION: Address */}
                <div className="col-12">
                  <div className="form-section-label">Delivery Address</div>
                </div>

                {/* Door */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="doorNo">House / Flat / Door No</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="doorNo"
                      name="door"
                      maxLength="20"
                      value={formData.door}
                      onChange={handleChange}
                      placeholder="House / Flat / Door No"
                    />
                  </div>
                </div>

                {/* Street */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="street">Street / Road Name</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="street"
                      name="street"
                      maxLength="80"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Street / Road Name"
                    />
                  </div>
                </div>

                {/* Area */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="area">Area / Locality</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="area"
                      name="area"
                      maxLength="80"
                      value={formData.area}
                      onChange={handleChange}
                      placeholder="Area / Locality"
                    />
                  </div>
                </div>

                {/* Landmark */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <label htmlFor="landmark">Landmark (optional)</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="landmark"
                      name="landmark"
                      maxLength="80"
                      value={formData.landmark}
                      onChange={handleChange}
                      placeholder="Landmark"
                    />
                  </div>
                </div>

                {/* Pincode */}
                <div className="col-md-4">
                  <div className="form-floating pincode-wrapper">
                    <label htmlFor="pincode">Pincode</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="pincode"
                      name="pincode"
                      maxLength="6"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      value={formData.pincode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setPincodeValid(false);
                        setFormData({ ...formData, pincode: value, city: "", state: "" });
                        if (value.length === 6) fetchAddressFromPincode(value);
                      }}
                      placeholder="Pincode"
                    />
                    {pincodeValid && (
                      <span className="pincode-check visible" title="Auto-filled" />
                    )}
                  </div>
                </div>

                {/* City */}
                <div className="col-md-4">
                  <div className="form-floating form-floating-relative">
                    <label htmlFor="city">City / District</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City"
                      disabled={!formData.manual_entry}
                    />
                    {formData.city && !formData.manual_entry && (
                      <span className="autofill-badge">Auto</span>
                    )}
                  </div>
                </div>

                {/* State */}
                <div className="col-md-4">
                  <div className="form-floating" style={{ position: "relative" }}>
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="State"
                      disabled={!formData.manual_entry}
                    />
                    {formData.state && !formData.manual_entry && (
                      <span className="autofill-badge">Auto</span>
                    )}
                  </div>
                </div>

                {/* SECTION: Photo */}
                {user?.login_method !== "google" && (
                  <>
                    <div className="col-12">
                      <div className="form-section-label">Profile Photo</div>
                    </div>
                    <div className="col-12">
                      <div className="file-upload-wrapper">
                        <input
                          type="file"
                          className="form-control themed-input"
                          name="profile_image"
                          accept="image/*"
                          onChange={handleChange}
                        />
                      </div>
                      {preview && (
                        <div className="profile-img-preview-wrapper">
                          <img
                            src={preview}
                            alt="preview"
                            className="profile-preview-img"
                          />
                          <div className="profile-img-preview-text">
                            <strong>Preview</strong>
                            Square images look best. Max 2 MB recommended.
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* ── FOOTER ── */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button type="submit" className="btn-save">
                Save Profile
                <span>→</span>
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileUpdateForm;